import React from 'react';
import {
  MessageSquare,
  PhoneOutgoing,
  PhoneIncoming,
  PhoneMissed,
  PhoneOff,
  Users,
  Hash,
  Clock,
  Eye,
  CheckCircle,
} from 'lucide-react';
import {
  SectionCard,
  MetricCard,
  TabLoading,
  TabError,
  TabEmpty,
  StatusBadge,
} from './shared';
import { useUserChatSummary, useUserCalls } from '../../hooks/useUserTabs';
import { formatRelativeTime, formatNumber, formatDateTime } from '../../utils/helpers';
import type { ChatRoomParticipant, CallItem, CallUser } from '../../types/userTabs';

interface CommunicationTabProps {
  userId: number;
}

function getCallStatusVariant(status: string): 'success' | 'warning' | 'error' | 'info' | 'default' {
  switch (status.toUpperCase()) {
    case 'ENDED': return 'success';
    case 'ACCEPTED': return 'info';
    case 'RINGING': case 'PENDING': return 'warning';
    case 'MISSED': case 'DECLINED': case 'CANCELLED': case 'FAILED': return 'error';
    default: return 'default';
  }
}

function getCallDuration(call: CallItem): string | null {
  const start = call.acceptedAt || call.startedAt;
  if (!start || !call.endedAt) return null;
  const ms = new Date(call.endedAt).getTime() - new Date(start).getTime();
  if (ms <= 0) return null;
  const secs = Math.round(ms / 1000);
  if (secs < 60) return `${secs}s`;
  const mins = Math.floor(secs / 60);
  const rem = secs % 60;
  return `${mins}m ${rem}s`;
}

function getRoomTypeLabel(type: string): string {
  switch (type) {
    case 'DIRECT': return 'Direct Message';
    case 'GROUP': return 'Group Chat';
    default: return type;
  }
}

const CommunicationTab: React.FC<CommunicationTabProps> = ({ userId }) => {
  const { data: chatData, loading: chatLoading, error: chatError, refetch: chatRefetch } = useUserChatSummary(userId, true);
  const { data: callsData, loading: callsLoading, error: callsError, refetch: callsRefetch } = useUserCalls(userId, true);

  const loading = chatLoading || callsLoading;
  const error = chatError || callsError;

  if (loading) return <TabLoading />;
  if (error) {
    return (
      <TabError
        message={error}
        onRetry={() => {
          if (chatError) chatRefetch();
          if (callsError) callsRefetch();
        }}
      />
    );
  }

  if (!chatData && !callsData) {
    return <TabEmpty message="No communication data available" icon={MessageSquare} />;
  }

  const roomCount = chatData?.roomCount ?? 0;
  const messageCount = chatData?.messageCount ?? 0;
  const outgoingItems = callsData?.outgoingCalls?.items ?? [];
  const incomingItems = callsData?.incomingCalls?.items ?? [];
  const outgoingTotal = callsData?.outgoingCalls?.total ?? outgoingItems.length;
  const incomingTotal = callsData?.incomingCalls?.total ?? incomingItems.length;

  return (
    <div className="space-y-5">
      {/* Metrics */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <MetricCard
          label="Chat Rooms"
          value={formatNumber(roomCount)}
          icon={MessageSquare}
          color="text-blue-400"
          bgColor="bg-blue-500/10"
        />
        <MetricCard
          label="Messages Sent"
          value={formatNumber(messageCount)}
          icon={Hash}
          color="text-violet-400"
          bgColor="bg-violet-500/10"
        />
        <MetricCard
          label="Outgoing Calls"
          value={formatNumber(outgoingTotal)}
          icon={PhoneOutgoing}
          color="text-emerald-400"
          bgColor="bg-emerald-500/10"
        />
        <MetricCard
          label="Incoming Calls"
          value={formatNumber(incomingTotal)}
          icon={PhoneIncoming}
          color="text-amber-400"
          bgColor="bg-amber-500/10"
        />
      </div>

      {/* Recent Chat Rooms */}
      {chatData && (
        <SectionCard
          title="Recent Chat Rooms"
          icon={MessageSquare}
          subtitle={`${chatData.recentRooms.length} recent room${chatData.recentRooms.length !== 1 ? 's' : ''}`}
          collapsible
          defaultOpen
        >
          {chatData.recentRooms.length > 0 ? (
            <div className="space-y-2.5">
              {chatData.recentRooms.map((participant: ChatRoomParticipant) => {
                const room = participant.chatRoom;
                return (
                  <div
                    key={participant.id}
                    className="rounded-lg border border-border/50 bg-muted/5 p-4"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex items-center gap-3 min-w-0 flex-1">
                        <div className="p-2 rounded-lg bg-blue-500/10 flex-shrink-0">
                          <MessageSquare className="h-4 w-4 text-blue-400" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2">
                            <p className="text-sm font-medium text-foreground truncate">
                              {room.name || getRoomTypeLabel(room.type)}
                            </p>
                            <StatusBadge
                              status={participant.isActive ? 'Active' : 'Left'}
                              variant={participant.isActive ? 'success' : 'default'}
                            />
                          </div>
                          <div className="flex items-center flex-wrap gap-x-3 gap-y-1 mt-1">
                            <span className="flex items-center gap-1 text-[11px] text-muted-foreground">
                              <Users className="h-3 w-3" />
                              {room._count.participants} participant{room._count.participants !== 1 ? 's' : ''}
                            </span>
                            <span className="flex items-center gap-1 text-[11px] text-muted-foreground">
                              <Hash className="h-3 w-3" />
                              {formatNumber(room._count.messages)} message{room._count.messages !== 1 ? 's' : ''}
                            </span>
                            <span className="flex items-center gap-1 text-[11px] text-muted-foreground" title={formatDateTime(participant.lastSeenAt)}>
                              <Eye className="h-3 w-3" />
                              Seen {formatRelativeTime(participant.lastSeenAt)}
                            </span>
                          </div>
                        </div>
                      </div>
                      <div className="flex flex-col items-end gap-1 flex-shrink-0">
                        <span className="text-[10px] text-muted-foreground font-mono">Room #{room.id}</span>
                        <span className="flex items-center gap-1 text-[11px] text-muted-foreground" title={formatDateTime(participant.joinedAt)}>
                          <Clock className="h-3 w-3" />
                          Joined {formatRelativeTime(participant.joinedAt)}
                        </span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="flex items-center justify-center py-6">
              <p className="text-sm text-muted-foreground">No recent chat rooms</p>
            </div>
          )}
        </SectionCard>
      )}

      {/* Outgoing Calls */}
      {callsData && (
        <SectionCard
          title="Outgoing Calls"
          icon={PhoneOutgoing}
          subtitle={`${outgoingTotal} call${outgoingTotal !== 1 ? 's' : ''}`}
          collapsible
          defaultOpen={outgoingItems.length > 0}
        >
          {outgoingItems.length > 0 ? (
            <div className="space-y-2.5">
              {outgoingItems.map((call) => (
                <CallCard key={call.id} call={call} direction="outgoing" />
              ))}
            </div>
          ) : (
            <div className="flex items-center justify-center py-6">
              <p className="text-sm text-muted-foreground">No outgoing calls</p>
            </div>
          )}
        </SectionCard>
      )}

      {/* Incoming Calls */}
      {callsData && (
        <SectionCard
          title="Incoming Calls"
          icon={PhoneIncoming}
          subtitle={`${incomingTotal} call${incomingTotal !== 1 ? 's' : ''}`}
          collapsible
          defaultOpen={incomingItems.length > 0}
        >
          {incomingItems.length > 0 ? (
            <div className="space-y-2.5">
              {incomingItems.map((call) => (
                <CallCard key={call.id} call={call} direction="incoming" />
              ))}
            </div>
          ) : (
            <div className="flex items-center justify-center py-6">
              <p className="text-sm text-muted-foreground">No incoming calls</p>
            </div>
          )}
        </SectionCard>
      )}
    </div>
  );
};

// ============================================================================
// Call Card
// ============================================================================

const CallCard: React.FC<{ call: CallItem; direction: 'incoming' | 'outgoing' }> = ({ call, direction }) => {
  const otherUser: CallUser | undefined = direction === 'outgoing' ? call.callee : call.caller;
  const duration = getCallDuration(call);
  const statusUpper = call.status.toUpperCase();

  const StatusIcon =
    statusUpper === 'MISSED' ? PhoneMissed
    : statusUpper === 'DECLINED' || statusUpper === 'CANCELLED' ? PhoneOff
    : statusUpper === 'ENDED' ? CheckCircle
    : direction === 'outgoing' ? PhoneOutgoing
    : PhoneIncoming;

  return (
    <div className="rounded-lg border border-border/50 bg-muted/5 p-4">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          {/* Avatar */}
          <div className="h-9 w-9 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0 overflow-hidden">
            {otherUser?.profileImage ? (
              <img src={otherUser.profileImage} alt="" className="h-full w-full object-cover" />
            ) : (
              <span className="text-xs font-semibold text-primary">
                {(otherUser?.name || '?').charAt(0).toUpperCase()}
              </span>
            )}
          </div>
          <div className="min-w-0">
            <p className="text-sm font-medium text-foreground truncate">
              {otherUser?.name || 'Unknown'}
            </p>
            {otherUser?.id && (
              <p className="text-[10px] text-muted-foreground font-mono">#{otherUser.id}</p>
            )}
          </div>
        </div>

        <div className="flex flex-col items-end gap-1.5 flex-shrink-0">
          <StatusBadge
            status={call.status.replace(/_/g, ' ')}
            variant={getCallStatusVariant(call.status)}
          />
          {duration && (
            <span className="text-[11px] text-muted-foreground font-medium">{duration}</span>
          )}
        </div>
      </div>

      {/* Meta row */}
      <div className="flex items-center flex-wrap gap-x-4 gap-y-1 mt-3 text-[11px] text-muted-foreground">
        <span className="flex items-center gap-1">
          <StatusIcon className="h-3 w-3" />
          <span className="capitalize">{direction}</span>
        </span>
        {call.endedReason && (
          <span className="capitalize">{call.endedReason.replace(/_/g, ' ').toLowerCase()}</span>
        )}
        <span className="flex items-center gap-1" title={formatDateTime(call.createdAt)}>
          <Clock className="h-3 w-3" />
          {formatRelativeTime(call.createdAt)}
        </span>
        <span className="text-[10px] font-mono">#{call.id}</span>
      </div>
    </div>
  );
};

export default CommunicationTab;
